import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import Select from "../../../shared/ui/Field/Select";
import Checkbox from "../../../shared/ui/Field/Checkbox";
import CompanyUkrainianDateField from "./CompanyUkrainianDateField";
import {
  filterParamFromYmd,
  ymdFromFilterParam,
} from "../lib/ukrainianDateEditor";
import { messageFromError } from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  isActiveMembership,
  isAdminMembership,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";
import CompanyWorkViewSwitch from "./CompanyWorkViewSwitch";
import {
  fetchCompanyTasks,
  fetchCompanyViewings,
} from "../api/companyWork";
import {
  executorLabel,
  formatDateTime,
  isCorporateWorkWritesAvailable,
  isWorkOverdue,
  linkedClientLabel,
  linkedPropertyLabel,
  memberDisplayName,
  membershipGenerationOf,
  priorityLabel,
  taskStatusLabel,
  viewingStatusLabel,
} from "../lib/workLabels";
import {
  buildTaskListParams,
  buildViewingListParams,
  clearedToolbarWorkFilters,
  countSecondaryWorkFilters,
  readWorkListQuery,
  secondaryWorkFilterSummary,
  writeWorkListSearch,
  TASK_STATUSES,
  VIEWING_STATUSES,
} from "../lib/workQuery";
import CompanyWorkCreateModal from "./CompanyWorkCreateModal";

function unwrapPage(data) {
  if (Array.isArray(data)) {
    return { results: data, count: data.length, next: null, previous: null };
  }
  return {
    results: Array.isArray(data?.results) ? data.results : [],
    count: typeof data?.count === "number" ? data.count : 0,
    next: data?.next ?? null,
    previous: data?.previous ?? null,
  };
}

function unwrapMembers(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

function CompanyWorkListContent() {
  const { companyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);

  const filters = readWorkListQuery(location.search);
  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [members, setMembers] = useState([]);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [state, setState] = useState("loading");
  const stateRef = useRef("loading");
  const [error, setError] = useState(null);
  const [createKind, setCreateKind] = useState(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  const [actionError, setActionError] = useState(null);
  stateRef.current = state;
  writesSuspendedRef.current = writesSuspended;
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    if (filters.create === "task" || filters.create === "viewing") {
      setCreateKind(filters.create);
    }
  }, [filters.create]);

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const writesAvailable =
    authorityMatches && isCorporateWorkWritesAvailable(company);
  const canCreate =
    writesAvailable && isActiveMembership(company, membership);
  const isOa = isAdminMembership(company, membership);
  const extraCount = countSecondaryWorkFilters(filters);
  const extraSummary = secondaryWorkFilterSummary(filters);

  const replaceFilters = useCallback(
    (next) => {
      navigate(
        {
          pathname: `/companies/${companyId}/tasks`,
          search: writeWorkListSearch({ ...filters, ...next }),
        },
        { replace: true },
      );
    },
    [companyId, filters, navigate],
  );

  const clearProtected = useCallback(() => {
    setRows([]);
    setCount(0);
    setMembers([]);
    setCreateKind(null);
    setError(null);
  }, []);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    let op = scopeGate.beginRead({
      companyId,
      actorId,
      resourceId: `list:${filters.kind}`,
    });
    const keepVisible = background && stateRef.current === "ready";
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const mineMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId)
          ? mine.membership
          : null;
      const nextGeneration = membershipGenerationOf(mineMatches);
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);

      scopeGate.bindIdentity({
        companyId,
        actorId,
        membershipGeneration: nextGeneration,
        resourceId: `list:${filters.kind}`,
        bindMembershipGeneration: true,
        bindResourceId: true,
      });

      if (!isActiveMembership(companyData, mineMatches)) {
        clearProtected();
        setState("denied");
        return;
      }

      op = scopeGate.beginRead({
        companyId,
        actorId,
        membershipGeneration: nextGeneration,
        resourceId: `list:${filters.kind}`,
      });

      if (isAdminMembership(companyData, mineMatches)) {
        try {
          const memberData = await fetchMembers(companyId, "active");
          if (scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
            setMembers(unwrapMembers(memberData));
          }
        } catch {
          /* ignore */
        }
      }

      const listParams =
        filters.kind === "viewing"
          ? buildViewingListParams(filters)
          : buildTaskListParams(filters);
      const pageData =
        filters.kind === "viewing"
          ? await fetchCompanyViewings(companyId, listParams)
          : await fetchCompanyTasks(companyId, listParams);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const page = unwrapPage(pageData);
      setRows(page.results);
      setCount(page.count);
      revalidationConfirmed = true;
      setActionError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
      setState("ready");
    } catch (err) {
      if (
        op &&
        !scopeGate.readStillValid(op, { mounted: mountedRef.current })
      ) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        setActionError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      if (status === 404 || status === 403) {
        clearProtected();
        setState("denied");
        return;
      }
      clearProtected();
      setError(messageFromError(err));
      setState("error");
    } finally {
      settleRevalidationWrites({
        keepVisible: keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [
    actorId,
    clearProtected,
    companyId,
    filters.kind,
    filters.page,
    filters.status,
    filters.responsible_user,
    filters.client,
    filters.property,
    filters.overdue,
    filters.due_after,
    filters.due_before,
    filters.scheduled_after,
    filters.scheduled_before,
    scopeGate,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    // Bind current list resource BEFORE authority discovery so a prior
    // list:task (etc.) cannot reject the new beginRead via stale resourceId.
    scopeGate.bindIdentity({
      companyId,
      actorId,
      resourceId: `list:${filters.kind}`,
      bindResourceId: true,
    });
    load();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
    };
  }, [
    companyId,
    actorId,
    filters.kind,
    filters.page,
    filters.status,
    filters.responsible_user,
    filters.client,
    filters.property,
    filters.overdue,
    filters.due_after,
    filters.due_before,
    filters.scheduled_after,
    filters.scheduled_before,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready",
    busy: createBusy,
    onRevalidate: () => load({ background: true }),
  });

  const pageSize = 20;
  const totalPages = Math.max(1, Math.ceil(count / pageSize));
  const calendarSearch = (() => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.responsible_user) {
      params.set("responsible_user", filters.responsible_user);
    }
    if (filters.client) params.set("client", filters.client);
    if (filters.property) params.set("property", filters.property);
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  })();

  return (
    <section
      className="clients-page feature-company-workspace feature-company-work"
      id="Csection"
      tabIndex="-1"
    >
      <div className="container">
      <CompanyPageHeader
        title="Задачі та перегляди"
        subtitle="Робота компанії — окремо від особистих задач"
        company={authorityMatches ? company : null}
        membership={authorityMatches ? membership : null}
        companyId={companyId}
        activeNav="tasks"
        actions={
          canCreate ? (
            <>
              <button
                type="button"
                className="btn-more"
                disabled={writesSuspended}
                onClick={() => {
                  if (writesSuspended) return;
                  setCreateKind("task");
                }}
              >
                Нова задача
              </button>
              <button
                type="button"
                className="action-btn"
                disabled={writesSuspended}
                onClick={() => {
                  if (writesSuspended) return;
                  setCreateKind("viewing");
                }}
              >
                Новий перегляд
              </button>
            </>
          ) : null
        }
      />

      <CompanyWorkViewSwitch
        activeView="list"
        listHref={`/companies/${companyId}/tasks${calendarSearch}`}
        calendarHref={`/companies/${companyId}/tasks/calendar${calendarSearch}`}
      />

      {!writesAvailable && authorityMatches && state === "ready" ? (
        <p className="feature-company-workspace__meta" role="status">
          Запис вимкнено. Читання доступне.
        </p>
      ) : null}

      <div className="feature-company-work__filters">
        <div
          className="feature-company-work__kind-switch"
          role="tablist"
          aria-label="Тип роботи"
        >
          <button
            type="button"
            role="tab"
            aria-selected={filters.kind === "task"}
            className={[
              "feature-company-work__kind-switch-btn",
              filters.kind === "task"
                ? "feature-company-work__kind-switch-btn--active"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() =>
              replaceFilters({
                kind: "task",
                page: 1,
                status: "",
                scheduled_after: "",
                scheduled_before: "",
              })
            }
          >
            Задачі
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={filters.kind === "viewing"}
            className={[
              "feature-company-work__kind-switch-btn",
              filters.kind === "viewing"
                ? "feature-company-work__kind-switch-btn--active"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            onClick={() =>
              replaceFilters({
                kind: "viewing",
                page: 1,
                status: "",
                overdue: false,
                due_after: "",
                due_before: "",
              })
            }
          >
            Перегляди
          </button>
        </div>
        <div
          className={[
            "feature-company-work__filter-toolbar",
            "feature-company-work__filter-primary",
            isOa ? "feature-company-work__filter-toolbar--with-executor" : "",
            filters.kind === "task"
              ? "feature-company-work__filter-toolbar--task"
              : "feature-company-work__filter-toolbar--viewing",
            filtersOpen ? "is-open" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
        <div className="feature-company-work__filter-status-row">
        <label className="ui-form__field">
          <span>Статус</span>
          <Select
            value={filters.status}
            onChange={(e) => replaceFilters({ status: e.target.value, page: 1 })}
            options={[
              { value: "", label: "Усі" },
              ...(filters.kind === "viewing" ? VIEWING_STATUSES : TASK_STATUSES).map(
                (s) => ({
                  value: s,
                  label:
                    filters.kind === "viewing"
                      ? viewingStatusLabel(s)
                      : taskStatusLabel(s),
                }),
              ),
            ]}
          />
        </label>
        <button
          type="button"
          className="feature-company-work__filters-toggle"
          aria-expanded={filtersOpen}
          aria-controls="company-work-filter-extra"
          onClick={() => setFiltersOpen((open) => !open)}
        >
          Фільтри
          {extraCount > 0 ? ` · ${extraCount}` : ""}
        </button>
        </div>
        <div
          id="company-work-filter-extra"
          className={[
            "feature-company-work__filter-controls",
            "feature-company-work__filter-extra",
            filtersOpen ? "is-open" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
        {isOa ? (
          <label className="ui-form__field">
            <span>Виконавець</span>
            <Select
              value={filters.responsible_user}
              onChange={(e) =>
                replaceFilters({ responsible_user: e.target.value, page: 1 })
              }
              options={[
                { value: "", label: "Усі" },
                { value: "unassigned", label: "Непризначені" },
                ...(actorId != null
                  ? [{ value: String(actorId), label: "Мої" }]
                  : []),
                ...members
                  .map((m) => {
                    const userId = m.user?.id ?? m.user_id;
                    if (userId == null) return null;
                    if (actorId != null && String(userId) === String(actorId)) {
                      return null;
                    }
                    return {
                      value: String(userId),
                      label: memberDisplayName(m) || `Учасник #${userId}`,
                    };
                  })
                  .filter(Boolean),
              ]}
            />
          </label>
        ) : null}
        {filters.kind === "task" ? (
          <>
            <div className="ui-form__field feature-company-work__filter-date">
              <span>Дедлайн від</span>
              <CompanyUkrainianDateField
                id="company-work-filter-due-after"
                mode="date"
                canonicalValue={ymdFromFilterParam(filters.due_after)}
                ariaLabel="Дедлайн від"
                onCanonicalChange={(ymd) =>
                  replaceFilters({
                    due_after: filterParamFromYmd(ymd, "start"),
                    page: 1,
                  })
                }
              />
            </div>
            <div className="ui-form__field feature-company-work__filter-date">
              <span>Дедлайн до</span>
              <CompanyUkrainianDateField
                id="company-work-filter-due-before"
                mode="date"
                canonicalValue={ymdFromFilterParam(filters.due_before)}
                ariaLabel="Дедлайн до"
                onCanonicalChange={(ymd) =>
                  replaceFilters({
                    due_before: filterParamFromYmd(ymd, "end"),
                    page: 1,
                  })
                }
              />
            </div>
            <Checkbox
              id="company-work-filter-overdue"
              className="feature-company-work__overdue"
              label="Прострочені"
              checked={Boolean(filters.overdue)}
              onChange={(e) =>
                replaceFilters({ overdue: e.target.checked, page: 1 })
              }
            />
          </>
        ) : (
          <>
            <div className="ui-form__field feature-company-work__filter-date">
              <span>Заплановано від</span>
              <CompanyUkrainianDateField
                id="company-work-filter-scheduled-after"
                mode="date"
                canonicalValue={ymdFromFilterParam(filters.scheduled_after)}
                ariaLabel="Заплановано від"
                onCanonicalChange={(ymd) =>
                  replaceFilters({
                    scheduled_after: filterParamFromYmd(ymd, "start"),
                    page: 1,
                  })
                }
              />
            </div>
            <div className="ui-form__field feature-company-work__filter-date">
              <span>Заплановано до</span>
              <CompanyUkrainianDateField
                id="company-work-filter-scheduled-before"
                mode="date"
                canonicalValue={ymdFromFilterParam(filters.scheduled_before)}
                ariaLabel="Заплановано до"
                onCanonicalChange={(ymd) =>
                  replaceFilters({
                    scheduled_before: filterParamFromYmd(ymd, "end"),
                    page: 1,
                  })
                }
              />
            </div>
          </>
        )}
        {extraCount > 0 || filters.status ? (
          <button
            type="button"
            className="action-btn action-btn--ghost"
            onClick={() => replaceFilters(clearedToolbarWorkFilters(filters))}
          >
            Скинути
          </button>
        ) : null}
        </div>
        </div>
        {!filtersOpen && extraSummary.length > 0 ? (
          <p className="feature-company-work__filter-summary">
            Активні: {extraSummary.join(" · ")}
          </p>
        ) : null}
      </div>

      {actionError && state === "ready" ? (
        <p className="feature-company-workspace__error" role="alert">
          {actionError}
        </p>
      ) : null}

      {state === "loading" ? (
        <Spinner label="Завантаження" />
      ) : null}
      {state === "error" ? (
        <EmptyState
          title="Не вдалося завантажити"
          description={error || "Спробуйте ще раз."}
          primaryAction={
            <button type="button" className="action-btn" onClick={load}>
              Повторити
            </button>
          }
        />
      ) : null}
      {state === "denied" ? (
        <EmptyState
          title="Немає доступу"
          description="Потрібне активне членство компанії."
        />
      ) : null}
      {state === "ready" && rows.length === 0 ? (
        <EmptyState
          title={filters.kind === "viewing" ? "Немає переглядів" : "Немає задач"}
          description="Список порожній для поточного фільтра."
        />
      ) : null}
      {state === "ready" && rows.length > 0 ? (
        <ul className="feature-company-work__rows" aria-label="Список роботи">
          {rows.map((row) => {
            const overdue = isWorkOverdue(row, filters.kind);
            const to =
              filters.kind === "viewing"
                ? `/companies/${companyId}/viewings/${row.id}`
                : `/companies/${companyId}/tasks/${row.id}`;
            const titleText =
              filters.kind === "viewing"
                ? `Перегляд #${row.id}`
                : row.title || `Задача #${row.id}`;
            const scheduleText =
              filters.kind === "viewing"
                ? formatDateTime(row.scheduled_at)
                : row.due_date
                  ? formatDateTime(row.due_date)
                  : "Без дедлайну";
            return (
              <li
                key={`${filters.kind}-${row.id}`}
                className={[
                  "feature-company-work__row",
                  overdue ? "feature-company-work__row--overdue" : null,
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <Link to={to} className="feature-company-work__row-link">
                  <span className="feature-company-work__row-kind">
                    {filters.kind === "viewing" ? "Перегляд" : "Задача"}
                  </span>
                  <span className="feature-company-work__row-title">
                    {titleText}
                  </span>
                  <span className="feature-company-work__row-meta">
                    <span className="feature-company-work__row-status">
                      {filters.kind === "viewing"
                        ? viewingStatusLabel(row.status)
                        : taskStatusLabel(row.status)}
                      {filters.kind === "task"
                        ? ` · ${priorityLabel(row.priority)}`
                        : ""}
                      {overdue ? " · Прострочено" : ""}
                    </span>
                    <span className="feature-company-work__row-schedule">
                      {scheduleText}
                    </span>
                    <span className="feature-company-work__row-executor">
                      {executorLabel(row.responsible_user_id, members)}
                    </span>
                    <span className="feature-company-work__row-links">
                      {linkedClientLabel(row.linked_client)}
                      {" · "}
                      {linkedPropertyLabel(row.linked_property)}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}

      {state === "ready" && totalPages > 1 ? (
        <NumberedPagination
          currentPage={filters.page}
          totalPages={totalPages}
          hasPrevious={filters.page > 1}
          hasNext={filters.page < totalPages}
          onPageChange={(page) => replaceFilters({ page })}
          ariaLabel="Сторінки роботи компанії"
        />
      ) : null}

      {canCreate && createKind != null ? (
        <CompanyWorkCreateModal
          open
          kind={createKind}
          writesSuspended={writesSuspended}
          onBusyChange={setCreateBusy}
          companyId={companyId}
          company={company}
          membership={membership}
          actorId={actorId}
          initialClientId={filters.client || null}
          initialPropertyId={filters.property || null}
          onClose={() => {
            setCreateKind(null);
            if (filters.create) {
              replaceFilters({ create: "" });
            }
          }}
          onCreated={(created, kind) => {
            setCreateKind(null);
            const path =
              kind === "viewing"
                ? `/companies/${companyId}/viewings/${created.id}`
                : `/companies/${companyId}/tasks/${created.id}`;
            navigate(path);
          }}
        />
      ) : null}
      </div>
    </section>
  );
}

export default CompanyWorkListContent;
